import React, { useState, useEffect, useRef } from 'react';
import { useClassName, useWindowSize } from 'common/hooks';
import './things-to-do.less';
import {
  getContentfulAssetUrlandAlt,
  getContentfulField,
} from 'common/components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { LinkTabs } from 'public/components/Tabs/Tabs';
import { Button } from 'common/lazy/SemanticUI';
import { OperationalHoursModal } from 'public/screens/CustomLandingPage/Components/OperationalHoursModal';
import { getContentRides } from 'public/components/AttractionScreen/RideClosures';
import { DateTime } from 'luxon';
import useCustomStyle from 'public/screens/CustomLandingPage/hooks';
import { isVenueDateUnavailable } from 'utils/helpers/openingHours';

const ROTATION_INTERVAL = 10000;

export const ThingsToDo = ({ venue }) => {
  const className = useClassName('ThingsToDo');
  const { isMobile, isTablet, isDesktop } = useWindowSize();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const slidesContainerRef = useRef(null);
  const slideRefs = useRef([]);

  const mainColor = getContentfulField(venue.parkScreen?.mainColor);
  const tertiaryColor = getContentfulField(venue.parkScreen?.tertiaryColor);
  const thingsToDo = getContentfulField(venue.parkScreen?.thingsToDo);

  const name = getContentfulField(thingsToDo.fields.name);
  const settings = getContentfulField(thingsToDo.fields.settings);
  const description = settings?.find(
    (setting) => getContentfulField(setting.fields.key) === 'description'
  );
  const descriptionValue = description
    ? getContentfulField(description?.fields.richTextValue)
    : null;

  const slides = getContentfulField(thingsToDo.fields.slides);
  const mappedSlides = slides.map((slide) => {
    const name = getContentfulField(slide.fields.name);
    const description = getContentfulField(slide.fields.description);
    const icon = getContentfulField(
      getContentfulField(slide.fields.icon).fields.file
    ).url;
    // const logoOverlay = getContentfulField(
    //   getContentfulField(slide.fields.logoOverlay).fields.file
    // ).url;
    const assetFields = getContentfulField(slide.fields.asset).fields;

    const asset = {
      mobile: getContentfulAssetUrlandAlt(assetFields.mobile),
      desktop: getContentfulAssetUrlandAlt(assetFields.desktop),
    };
    const linksItems = getContentfulField(slide.fields.links);
    const links = (linksItems || []).map((link) => {
      return {
        url: getContentfulField(link.fields.url),
        label: getContentfulField(link.fields.label),
        type: getContentfulField(link.fields.type),
        theme: getContentfulField(link.fields.theme),
      };
    });
    const leftLink = links.find((link) => link.theme === 'light');
    const rightLink = links.find((link) => link.theme === 'dark');

    return {
      name,
      description,
      icon,
      logoOverlay: null, // required from jess
      asset,
      links: [leftLink, rightLink].filter(Boolean),
    };
  });

  const links = mappedSlides.map((slide) => {
    return {
      url: slide.name,
      title: slide.name,
    };
  });

  const localDate = DateTime.local();
  const [date, setDate] = useState(localDate);

  const ridesCards = getContentRides(venue);

  const openingHoursBanner = getContentfulField(
    venue?.parkScreen?.openingHoursBanner
  );

  const openingHoursBannerFields = getContentfulField(
    venue.parkScreen.openingHoursBanner
  )?.fields;
  const emptyAsset = getContentfulField(openingHoursBannerFields?.logoOverlay);
  const settingsField = getContentfulField(openingHoursBannerFields?.settings);

  const rawSettings =
    settingsField?.mobile || settingsField?.tablet || settingsField?.desktop
      ? (isMobile && settingsField.mobile) ||
        (isTablet && settingsField.tablet) ||
        (isDesktop && settingsField.desktop)
      : settingsField;

  const processedSettings = (Array.isArray(rawSettings) ? rawSettings : []).map(
    (setting) => ({
      ...setting,
      fields: {
        key: getContentfulField(setting.fields?.key),
        name: getContentfulField(setting.fields?.name),
        value: getContentfulField(setting.fields?.value),
        mobileValue: getContentfulField(setting.fields?.mobileValue),
        tabletValue: getContentfulField(setting.fields?.tabletValue),
        desktopValue: getContentfulField(setting.fields?.desktopValue),
      },
    })
  );

  const [, style] = useCustomStyle(processedSettings);
  const emptyAssetStyle = Object.entries(style).reduce((acc, [key, value]) => {
    if (key.startsWith('--logo-overlay-')) {
      acc[key.replace('--logo-overlay-', '')] = value;
    }
    return acc;
  }, {});

  const assetEntry = openingHoursBanner?.fields?.asset
    ? getContentfulField(openingHoursBanner.fields.asset)
    : null;

  const mappedOpeningHoursBanner = !openingHoursBanner
    ? null
    : {
        name: getContentfulField(openingHoursBanner?.fields?.name),
        hideLinearGradient: getContentfulField(
          openingHoursBanner?.fields?.hideLinearGradient
        ),
        asset: assetEntry
          ? {
              name: getContentfulField(assetEntry?.fields?.name),
              mobile: assetEntry?.fields?.mobile
                ? getContentfulAssetUrlandAlt(assetEntry.fields.mobile)
                : null,
              tablet: assetEntry?.fields?.tablet
                ? getContentfulAssetUrlandAlt(assetEntry.fields.tablet)
                : null,
              desktop: assetEntry?.fields?.desktop
                ? getContentfulAssetUrlandAlt(assetEntry.fields.desktop)
                : null,
            }
          : null,
      };

  const [unavailableDates, setUnavailableDates] = useState([]);

  useEffect(() => {
    if (!venue) return;
    const unavailableDates = venue?.priceTypes?.reduce(
      (acc, { date, type }) => (type === 'closed' ? acc.concat(date) : acc),
      []
    );
    setUnavailableDates(unavailableDates);
  }, [venue]);

  const isDateUnavailable = (isoDate) =>
    isVenueDateUnavailable(venue, isoDate, unavailableDates);
  const [openHoursModal, setOpenHoursModal] = useState(false);

  // Auto-rotate tabs
  useEffect(() => {
    if (links.length === 0) return;
    const timer = setInterval(() => {
      setSelectedIndex((prevIndex) => (prevIndex + 1) % links.length);
    }, ROTATION_INTERVAL);

    return () => clearInterval(timer);
  }, [selectedIndex, links.length]);

  useEffect(() => {
    const updateHeight = () => {
      if (slidesContainerRef.current) {
        slidesContainerRef.current.style.height = 'auto';
        slideRefs.current.forEach((slide) => {
          if (slide) {
            slide.style.height = 'auto';
          }
        });

        let maxHeight = 0;

        slideRefs.current.forEach((slide) => {
          if (slide) {
            maxHeight = Math.max(maxHeight, slide.offsetHeight);
          }
        });

        if (maxHeight > 0) {
          requestAnimationFrame(() => {
            if (slidesContainerRef.current) {
              slidesContainerRef.current.style.height = `${maxHeight}px`;
            }
            slideRefs.current.forEach((slide) => {
              if (slide) {
                slide.style.height = `${maxHeight}px`;
              }
            });
          });
        }
      }
    };

    const timeoutId = setTimeout(updateHeight, 50);
    window.addEventListener('resize', updateHeight);

    const allImages = [];
    slideRefs.current.forEach((slide) => {
      if (slide) {
        const images = slide.querySelectorAll('img');
        images.forEach((img) => {
          allImages.push(img);
          if (!img.complete) {
            img.addEventListener('load', updateHeight);
          }
        });
      }
    });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateHeight);
      allImages.forEach((img) => {
        img.removeEventListener('load', updateHeight);
      });
    };
  }, [mappedSlides]);

  return (
    <div
      id="things-to-do"
      className={className('container')}
      style={{
        background: tertiaryColor,
      }}>
      <div
        className={className('container__wrapper')}
        style={{ backgroundColor: mainColor }}>
        <div className={className('container__inner')}>
          <div className={className('container__header')}>
            <h2 className={className('container__header__title')}>{name}</h2>
            <div className={className('container__header__description')}>
              {documentToReactComponents(descriptionValue)}
            </div>
          </div>
          <div className={className('container__links')}>
            {
              <LinkTabs
                styles={{
                  container: {
                    backgroundColor: mainColor,
                  },
                  linkClassName: className(
                    'container__links__link',
                    'animated'
                  ),
                }}
                links={links}
                isSelected={(index) => index === selectedIndex}
                fixed={false}
                onSelect={(e, index) => {
                  e.preventDefault();
                  setSelectedIndex(index);
                }}
              />
            }
          </div>
          <div
            className={className('container__slides')}
            ref={slidesContainerRef}>
            {mappedSlides.map((slide, index) => (
              <div
                ref={(el) => {
                  if (el) slideRefs.current[index] = el;
                }}
                className={className(
                  'container__slides__item',
                  index === selectedIndex ? 'selected' : ''
                )}
                key={index}>
                <div className={className('container__slides__item__content')}>
                  <div
                    className={className(
                      'container__slides__item__content__title-container'
                    )}>
                    <div
                      className={className(
                        'container__slides__item__content__icon'
                      )}>
                      <img src={slide.icon} alt={slide.name} />
                    </div>
                    <h3
                      className={className(
                        'container__slides__item__content__title'
                      )}>
                      {slide.name}
                    </h3>
                  </div>
                  <div
                    className={className(
                      'container__slides__item__content__description'
                    )}>
                    {documentToReactComponents(slide.description)}
                  </div>
                  {slide.links.length > 0 && (
                    <div
                      className={className(
                        'container__slides__item__content__links'
                      )}>
                      {slide.links.map((link) => {
                        if (link.url === '#operational-hours') {
                          return (
                            <OperationalHoursModal
                              key={link.url}
                              ridesCards={ridesCards}
                              isDateUnavailable={isDateUnavailable}
                              open={openHoursModal}
                              openingHours={venue.openingHours}
                              trigger={
                                <Button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setOpenHoursModal(true);
                                  }}
                                  key={link.url}
                                  primary={link.theme === 'light'}
                                  content={link.label}
                                  className={className(
                                    'container__slides__item__content__links__button',
                                    link.theme === 'light'
                                      ? 'primary'
                                      : 'secondary'
                                  )}
                                />
                              }
                              onOpen={() => setOpenHoursModal(true)}
                              onClose={() => setOpenHoursModal(false)}
                              initialDate={date.toISO()}
                              updateSelectedDate={(jsDate) => {
                                if (jsDate instanceof Date) {
                                  setDate(DateTime.fromJSDate(jsDate));
                                }
                              }}
                              openingHoursBanner={mappedOpeningHoursBanner}
                              venueSlug={venue.slug}
                              venueId={venue.id}
                              emptyAsset={emptyAsset}
                              emptyAssetStyle={emptyAssetStyle}
                            />
                          );
                        }
                        return (
                          <Button
                            key={link.url}
                            href={link.url}
                            primary={link.theme === 'light'}
                            content={link.label}
                            target={
                              link.type === 'external' ? '_blank' : '_self'
                            }
                            className={className(
                              'container__slides__item__content__links__button',
                              link.theme === 'light' ? 'primary' : 'secondary'
                            )}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className={className('container__slides__item__media')}>
                  <img
                    src={slide.asset.desktop.assetUrl}
                    alt={slide.asset.desktop.assetAlt}
                    className={className(
                      'container__slides__item__media__image'
                    )}
                  />
                  {slide.logoOverlay && (
                    <img
                      src={slide.logoOverlay}
                      alt=""
                      className={className(
                        'container__slides__item__media__overlay'
                      )}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
